import { useState, useEffect } from 'react';
import {
  Container,
  Stack,
  Title,
  Paper,
  Button,
  Group,
  Text,
  Card,
  Select,
  SimpleGrid,
  TextInput,
  Switch,
} from '@mantine/core';
import { IconMail } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAppUser } from '../../hooks/useAppUser';
import { usePageTitle } from '../../hooks/usePageTitle';

export const AdminEmailsPage = () => {
  usePageTitle('Admin Emails | Meals You Love');
  const { user } = useAppUser();
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailType, setTestEmailType] = useState<string | null>('make-it-yours');
  // Fetched from the API rather than listed here. A hand-copied list silently
  // drifts from what the send endpoint accepts, which is how the past-due email
  // shipped sendable but unselectable.
  const [testEmailOptions, setTestEmailOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailFirstName, setTestEmailFirstName] = useState('');
  const [testEmailHasCreatedPlan, setTestEmailHasCreatedPlan] = useState(true);
  const [testEmailHousehold, setTestEmailHousehold] = useState<string>('family');

  // Prefill the test-email recipient with the admin's own address.
  useEffect(() => {
    if (user?.email) setTestEmailTo((prev) => prev || user.email!);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/test-email-types`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const data = await response.json();
        if (!cancelled) setTestEmailOptions(data.types ?? []);
      } catch (error) {
        if (cancelled) return;
        notifications.show({
          title: 'Could not load email types',
          message: 'The test email picker is empty. Reload to try again.',
          color: 'red',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleSendTestEmail = async () => {
    if (!user) return;
    if (!testEmailTo) {
      notifications.show({
        title: 'Email required',
        message: 'Enter a recipient address.',
        color: 'red',
      });
      return;
    }

    setSendingTestEmail(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/test-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: testEmailType,
          toEmail: testEmailTo,
          firstName: testEmailFirstName || undefined,
          hasCreatedPlan: testEmailHasCreatedPlan,
          household: testEmailHousehold,
        }),
      });

      if (response.ok) {
        notifications.show({
          title: 'Test email sent',
          message: `Sent "${testEmailType}" to ${testEmailTo}`,
          color: 'green',
        });
      } else {
        const error = await response.json();
        notifications.show({
          title: 'Error',
          message: error.error || 'Failed to send test email',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to send test email',
        color: 'red',
      });
    } finally {
      setSendingTestEmail(false);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Paper withBorder p="md" radius="lg">
        <Stack gap="md">
          <Title order={3}>Lifecycle Emails</Title>
          <Text size="sm" c="dimmed">
            Send a test render of a lifecycle/drip or welcome email to any
            address. Uses sample data and touches no user records.
          </Text>
          <Card withBorder radius="lg">
            <Stack gap="sm">
              <Text fw={500}>Send Test Email</Text>
              {/* One column on phones — three inputs forced into a single
                  row are unusably narrow there. */}
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                <Select
                  label="Email"
                  data={testEmailOptions}
                  value={testEmailType}
                  onChange={setTestEmailType}
                  allowDeselect={false}
                  disabled={testEmailOptions.length === 0}
                />
                <TextInput
                  label="Send to"
                  placeholder="you@example.com"
                  value={testEmailTo}
                  onChange={(e) => setTestEmailTo(e.currentTarget.value)}
                />
                <TextInput
                  label="First name (optional)"
                  placeholder="e.g., Alex"
                  value={testEmailFirstName}
                  onChange={(e) => setTestEmailFirstName(e.currentTarget.value)}
                />
              </SimpleGrid>
              {testEmailType === 'welcome' && (
                <Switch
                  label="Recipient has already created a plan"
                  description="Switches the welcome email's call-to-action between 'open your plan' and 'build your first plan'."
                  checked={testEmailHasCreatedPlan}
                  onChange={(e) =>
                    setTestEmailHasCreatedPlan(e.currentTarget.checked)
                  }
                />
              )}
              <Select
                label="Household"
                description="Previews the household-specific copy. Affects welcome, finish-onboarding, first-plan, and make-it-yours; ignored by other emails."
                data={[
                  { value: 'family', label: 'Family (has kids)' },
                  { value: 'household', label: 'Household (2+ adults, no kids)' },
                  { value: 'solo', label: 'Solo (1 adult, no kids)' },
                ]}
                value={testEmailHousehold}
                onChange={(v) => setTestEmailHousehold(v ?? 'family')}
                allowDeselect={false}
              />
              <Group justify="flex-end">
                <Button
                  leftSection={<IconMail size="1rem" />}
                  onClick={handleSendTestEmail}
                  loading={sendingTestEmail}
                >
                  Send test email
                </Button>
              </Group>
            </Stack>
          </Card>
        </Stack>
      </Paper>
    </Container>
  );
};
